// Import pages from organized folders
import {
  Login,
  Signup,
  OtpVerification,
  Onboarding,
  UpdateEmail,
  ForgotPassword,
  AcceptInvitation,
  ResetPassword
} from '../pages/auth';

import {
  SelectEmployee,
  EmployeeDashboard,
  MyDonations,
  MyDonationDetail,
  EmployeeProfile,
  ViewEmployer as EmployeeViewEmployer,
  Charities as EmployeeCharities,
  ViewCharity as EmployeeViewCharity
} from '../pages/employee';

import {
  Transactions,
  Payouts,
  Dashboard as EmployerDashboard,
  Employees,
  Profile as EmployerProfile,
  PayoutDetail as EmployerPayoutDetail,
  LeaveRequests,
  ViewLeaveRequest,
  UpdateLeaveRequest,
  Charities,
  ViewCharity,
  PaymentReport,
  EmployeeReport,
  OutstandingInvoices,
  PaidInvoiceDetail,
  EmployeesView,
  ApprovedRequests
} from '../pages/employer';

import {
  CharityDashboardPage,
  DonationRequests,
  DonationRequestDetail,
  DonorList,
  DonorDetail,
  PayoutDetail as CharityPayoutDetail,
  Payouts as CharityPayouts,
  PendingConfirmation as CharityPendingConfirmation,
  UpcomingPayouts,
  UpcomingPayoutDetail,
  ViewUser as CharityViewUser,
  ViewEmployer as CharityViewEmployer,
  CharityProfile as CharityProfilePage,
  EmployerPayouts
} from '../pages/charity';

import {
  Dashboard,
  NotFound,
  PlaceholderPage
} from '../pages/common';

import {
  AdminDashboardPage,
  EmployeeManagementPage,
  EmployerManagementPage,
  DonationList,
  ViewDonation,
  CharityList,
  CharityProfile as AdminCharityProfile,
  EmployeeProfilePage,
  EmployerProfilePage,
  UserManagementPage,
  UserProfilePage,
  DonationReportListing,
  PayrollComplianceReportListing,
  AuditLogReportListing,
  PayrollDeductionsPage,
  PayrollTransactionHistoryPage,
  ManualPayrollEntryPage,
  RefundAdjustDonationPage,
  VerificationCharitiesPage,
  VerificationEmployersPage,
  IncomingPaymentsPage,
  ReceivedPaymentsPage,
  ReceivedPaymentDetailPage,
  OutgoingPaymentsPage,
  PaymentDetailPage,
  IncomingPaymentDetailPage,
  UnconfirmedBankDetailsPage,
  PendingPayoutsPage,
  PendingConfirmationPage
} from '../pages/admin';

import RoleDashboard from '../components/RoleDashboard.svelte';
import RoleRedirect from '../components/RoleRedirect.svelte';
import { wrap } from 'svelte-spa-router/wrap';
import { get } from 'svelte/store';
import { isAuthenticated } from '../stores/auth.store';

// Auth condition for protected routes
const isAuthenticatedCondition = () => get(isAuthenticated);

export const unAuthenticatedRoutes = {
  '/': Login,
  '/login': Login,
  '/signup': Signup,
  '/forgot-password': ForgotPassword,
  '/otp-verification': OtpVerification,
  '/verify-email-change': UpdateEmail,
  '/verify-email-change/:token': UpdateEmail,
  '/accept-invitation/:uuid': AcceptInvitation,
  '/reset-password/:token': ResetPassword,
  '*': NotFound
}

// Use the wrap function from svelte-spa-router to prepare our routes
export const routes = {  
  // Root route for authenticated users - redirects to appropriate dashboard
  '/': wrap({
    component: RoleRedirect,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true }
  }),
  
  '/verify-email-change': UpdateEmail,
  '/verify-email-change/:token': UpdateEmail,

  // Protected routes
  '/dashboard': wrap({
    component: RoleDashboard,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true }
  }),

  '/charity-dashboard': wrap({
    component: CharityDashboardPage,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true }
  }),
  
  // Employee-specific dashboard route (redirects to /dashboard for consistency)
  '/employee-dashboard': wrap({
    component: RoleRedirect,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true }
  }),
  
  '/my-donations': wrap({
    component: MyDonations,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true }
  }),

  '/my-donation/:id': wrap({
    component: MyDonationDetail,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true }
  }),

  '/view-employer/:id': wrap({
    component: EmployeeViewEmployer,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'view-employer' }
  }),


  '/admin/employee-profile/:uuid': wrap({
    component: EmployeeProfilePage,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'employee-profile' }
  }),

  '/admin/users': wrap({
    component: UserManagementPage,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'users' }
  }),

      '/admin/user-profile/:uuid': wrap({
        component: UserProfilePage,
        conditions: [isAuthenticatedCondition],
        userData: { requiresAuth: true, page: 'user-profile' }
      }),

      '/admin/payments/incoming': wrap({
        component: IncomingPaymentsPage,
        conditions: [isAuthenticatedCondition],
        userData: { requiresAuth: true, page: 'incoming-payments' }
      }),

      '/admin/payments/received': wrap({
        component: ReceivedPaymentsPage,
        conditions: [isAuthenticatedCondition],
        userData: { requiresAuth: true, page: 'pending-payouts' }
      }),

      '/admin/payments/received/:uuid': wrap({
        component: ReceivedPaymentDetailPage,
        conditions: [isAuthenticatedCondition],
        userData: { requiresAuth: true, page: 'pending-payouts' }
      }),

      '/admin/payments/outgoing': wrap({
        component: OutgoingPaymentsPage,
        conditions: [isAuthenticatedCondition],
        userData: { requiresAuth: true, page: 'completed-payments' }
      }),

      '/admin/payment-detail/:uuid': wrap({
        component: PaymentDetailPage,
        conditions: [isAuthenticatedCondition],
        userData: { requiresAuth: true, page: 'payment-detail' }
      }),

      '/admin/payments/incoming/:uuid': wrap({
        component: IncomingPaymentDetailPage,
        conditions: [isAuthenticatedCondition],
        userData: { requiresAuth: true, page: 'incoming-payments' }
      }),

      '/admin/unconfirmed-bank-details': wrap({
        component: UnconfirmedBankDetailsPage,
        conditions: [isAuthenticatedCondition],
        userData: { requiresAuth: true, page: 'unconfirmed-bank-details' }
      }),

      '/admin/verification/charities': wrap({
        component: VerificationCharitiesPage,
        conditions: [isAuthenticatedCondition],
        userData: { requiresAuth: true, page: 'verification-charities' }
      }),

      '/admin/verification/employers': wrap({
        component: VerificationEmployersPage,
        conditions: [isAuthenticatedCondition],
        userData: { requiresAuth: true, page: 'verification-employers' }
      }),

      '/admin/pending-payouts': wrap({
        component: PendingPayoutsPage,
        conditions: [isAuthenticatedCondition],
        userData: { requiresAuth: true, page: 'pending-payouts' }
      }),

      '/admin/pending-confirmation': wrap({
        component: PendingConfirmationPage,
        conditions: [isAuthenticatedCondition],
        userData: { requiresAuth: true, page: 'pending-confirmation' }
      }),

  '/employer-profile/:id': wrap({
    component: EmployerProfilePage,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'employer-profile' }
  }),

  // Charity-specific routes
  '/donation-requests': wrap({
    component: DonationRequests,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'donation-requests' }
  }),

  '/donation-request/:id': wrap({
    component: DonationRequestDetail,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'donation-request-detail' }
  }),

  '/donors': wrap({
    component: DonorList,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'donors' }
  }),

  '/donor-detail/:type/:id': wrap({
    component: DonorDetail,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'donor-detail' }
  }),

  '/employer-payouts/:id': wrap({
    component: EmployerPayouts,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'employer-payouts' }
  }),

  '/charity/view-user/:id': wrap({
    component: CharityViewUser,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'charity-view-user' }
  }),

  '/charity/view-employer/:id': wrap({
    component: CharityViewEmployer,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'charity-view-employer' }
  }),

  '/transactions': wrap({
    component: Transactions,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'transactions' }
  }),

  '/upcoming-payouts': wrap({
    component: UpcomingPayouts,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'upcoming-payouts' }
  }),

  '/upcoming-payout-detail/:id': wrap({
    component: UpcomingPayoutDetail,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'upcoming-payout-detail' }
  }),

  '/payouts': wrap({
    component: CharityPayouts,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'donations-received' }
  }),

  '/pending-confirmation': wrap({
    component: CharityPendingConfirmation,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'pending-confirmation' }
  }),

  '/payout-detail/:id': wrap({
    component: CharityPayoutDetail,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'payout-detail' }
  }),

  // Employer-specific routes
  '/employees': wrap({
    component: Employees,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'employees' }
  }),

  '/employees/:id': wrap({
    component: EmployeesView,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'employees' }
  }),

  '/employer-profile': wrap({
    component: EmployerProfile,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'profile' }
  }),

  '/employer-dashboard': wrap({
    component: EmployerDashboard,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'dashboard' }
  }),

  '/leave-requests': wrap({
    component: LeaveRequests,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'leave-requests' }
  }),

  '/approved-requests': wrap({
    component: ApprovedRequests,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'approved-requests' }
  }),

  '/view-leave-request': wrap({
    component: ViewLeaveRequest,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'leave-requests' }
  }),

  '/update-leave-request': wrap({
    component: UpdateLeaveRequest,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'leave-requests' }
  }),

  // Employer charity routes (must come before employee routes to avoid conflicts)
  '/employer/charities': wrap({
    component: Charities,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'charities' }
  }),

  '/employer/view-charity/:id': wrap({
    component: ViewCharity,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'charities' }
  }),

  '/my-employers': wrap({
    component: SelectEmployee,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'my-employers' }
  }),

  '/charities': wrap({
    component: EmployeeCharities,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'charities' }
  }),

  '/view-charity/:id': wrap({
    component: EmployeeViewCharity,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'charities' }
  }),

  '/employer/payment-history': wrap({
    component: PaymentReport,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'payment-history' }
  }),
  '/employer/payment-history/:invoiceId': wrap({
    component: PaidInvoiceDetail,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'payment-history' }
  }),
  '/employer/outstanding-invoices': wrap({
    component: OutstandingInvoices,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'outstanding-invoices' }
  }),
  '/employer/employee-report': wrap({
    component: EmployeeReport,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'employee-report' }
  }),

  // Admin-specific routes
  '/users': wrap({
    component: PlaceholderPage,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'users' }
  }),

  '/employers': wrap({
    component: EmployerManagementPage,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'employers' }
  }),

  '/system-reports': wrap({
    component: PlaceholderPage,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'system-reports' }
  }),
  
  // Placeholder pages for the sidebar menu items
  '/select-employer': wrap({
    component: PlaceholderPage,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'select-employer' }
  }),
  
  '/invite-employer': wrap({
    component: PlaceholderPage,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'invite-employer' }
  }),
  
  '/reports': wrap({
    component: PlaceholderPage,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'reports' }
  }),
  
  '/profile': wrap({
    component: EmployeeProfile,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'profile' }
  }),
  
  '/help-center': wrap({
    component: PlaceholderPage,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'help-center' }
  }),
  
  '/settings': wrap({
    component: PlaceholderPage,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'settings' }
  }),

  '/admin-dashboard': wrap({
    component: AdminDashboardPage,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'admin-dashboard' }
  }),

  '/manage-employees': wrap({
    component: EmployeeManagementPage,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'manage-employees' }
  }),
  '/donation-list': wrap({
    component: DonationList,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'donation-list' }
  }),

  '/view-donation/:id': wrap({
    component: ViewDonation,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'view-donation' }
  }),
  '/charity-list': wrap({
    component: CharityList,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'charity-list' }
  }),
  '/charity-profile/sign-in-email': wrap({
    component: CharityProfilePage,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'charity-profile' }
  }),
  '/charity-profile/:id': wrap({
    component: AdminCharityProfile,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'charity-profile' }
  }),

  '/charity-profile': wrap({
    component: CharityProfilePage,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'charity-profile' }
  }),
  '/charity-bank-account': wrap({
    component: CharityProfilePage,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'charity-bank-account' }
  }),
  '/donation-report': wrap({
    component: DonationReportListing,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'donation-report' }
  }), 
  '/payroll-compliance-reports': wrap({
    component: PayrollComplianceReportListing,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'payroll-compliance-reports' }
  }),
  '/audit-log-reports': wrap({
    component: AuditLogReportListing,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'audit-log-reports' }
  }), 
  '/payroll-deductions': wrap({
    component: PayrollDeductionsPage,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'payroll-deductions' }
  }),
  '/payroll-transaction-history': wrap({
    component: PayrollTransactionHistoryPage,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'payroll-transaction-history' }
  }),
  '/manual-payroll-entry': wrap({
    component: ManualPayrollEntryPage,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'manual-payroll-entry' }
  }),
  '/refund-adjust-donation': wrap({
    component: RefundAdjustDonationPage,
    conditions: [isAuthenticatedCondition],
    userData: { requiresAuth: true, page: 'refund-adjust-donation' }
  }),
  // Catch-all route for 404 Not Found
  '*': NotFound
};
